import Head from 'next/head'
import Script from 'next/script'
import { FaHeart, FaUsers, FaBalanceScale, FaEnvelope, FaExclamationCircle, FaHandsHelping } from 'react-icons/fa'
import { MdDiversity3 } from 'react-icons/md'
import { LegalPageLayout, LegalSection, LegalHighlight } from '../../components/index/LegalPageLayout'

export default function AntiDiscrimination() {
  const protectedCategories = [
    'Race, ethnicity, or national origin',
    'Religion, faith, or beliefs',
    'Gender identity, gender expression, or sexual orientation',
    'Age or generational affiliation',
    'Disability, physical or mental health status',
    'Marital, family, or socioeconomic status'
  ]

  const expectations = [
    'Treat one another with dignity and respect.',
    'Avoid using language or actions that discriminate or demean others.',
    'Uphold the principles of equality and fairness when interacting with others on the platform.'
  ]

  return (
    <>
      <Head>
        <title>Anti-Discrimination Policy | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no"/>
        <meta name="description" content="At StringsSG, we are committed to fostering an inclusive and respectful environment for all users, regardless of their background, identity, or beliefs."/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:url" content="https://www.stringssg.com/terms/anti-discrimination" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Anti-Discrimination Policy | StringsSG"/>
        <meta property="og:description" content="At StringsSG, we are committed to fostering an inclusive and respectful environment for all users, regardless of their background, identity, or beliefs." />
        <meta property="og:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/girl-with-stringssg-logo-on-mobile.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/girl-with-stringssg-logo-on-mobile.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/terms/anti-discrimination"/>
        <meta name="twitter:title" content="Anti-Discrimination Policy | StringsSG"/> 
        <meta name="twitter:description" content="At StringsSG, we are committed to fostering an inclusive and respectful environment for all users, regardless of their background, identity, or beliefs."/>
        <link rel="icon" href="/favicon.ico" />
        <link rel="canonical" href="https://www.stringssg.com/terms/anti-discrimination"/>
      </Head>
      
      <LegalPageLayout
        title="Anti-Discrimination Policy"
        subtitle="At StringsSG, we are committed to fostering an inclusive and respectful environment for all users, regardless of their background, identity, or beliefs."
        icon={MdDiversity3}
        iconBgColor="from-purple-500 to-indigo-600"
        heroBgColor="from-slate-900 via-purple-900 to-indigo-900"
      >
        <LegalHighlight color="blue">
          <div className="flex items-start gap-3">
            <FaHeart className="text-purple-600 text-xl mt-1 shrink-0" />
            <p>
              At StringsSG, we believe in creating a platform where everyone is treated with dignity and respect. Discrimination has no place in our community.
            </p>
          </div>
        </LegalHighlight>

        {/* Our Commitment */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <FaHandsHelping className="text-purple-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">Our Commitment to Inclusivity</h2>
          </div>
          <p className="text-slate-600 mb-4">StringsSG stands against all forms of discrimination, including but not limited to:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {protectedCategories.map((category, index) => (
              <div key={index} className="flex items-center gap-2 bg-purple-50 rounded-lg p-3">
                <span className="w-2 h-2 bg-purple-500 rounded-full shrink-0"></span>
                <span className="text-slate-700 text-sm">{category}</span>
              </div>
            ))}
          </div>
        </div>

        {/* What This Means */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FaUsers className="text-blue-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">What This Means for Our Users and Partners</h2>
          </div>
          <p className="text-slate-600 mb-4">We expect all members of the StringsSG community to:</p>
          <ul className="space-y-3">
            {expectations.map((expectation, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-blue-600 text-xs font-bold">{index + 1}</span>
                </span>
                <span className="text-slate-600">{expectation}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* What Happens */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <FaBalanceScale className="text-amber-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-900">What Happens in Cases of Discrimination?</h2>
          </div>
          <p className="text-slate-600 mb-4">StringsSG takes reports of discrimination seriously. We will:</p>
          <ul className="space-y-3 text-slate-600">
            <li className="flex items-start gap-3">
              <span className="text-amber-500">•</span>
              <span>Investigate all reported incidents promptly and fairly.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-amber-500">•</span>
              <span>Take appropriate action, which may include:
                <ul className="mt-2 ml-4 space-y-1 text-sm">
                  <li className="flex items-center gap-2"><span className="text-amber-400">-</span> Issuing warnings to involved parties</li>
                  <li className="flex items-center gap-2"><span className="text-amber-400">-</span> Temporary or permanent account suspension</li>
                  <li className="flex items-center gap-2"><span className="text-amber-400">-</span> Legal action where applicable</li>
                </ul>
              </span>
            </li>
          </ul>
        </div>

        {/* How to Report */}
        <div className="bg-linear-to-br from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <FaExclamationCircle className="text-white" />
            </div>
            <h2 className="text-lg font-bold">How to Report Discrimination</h2>
          </div>
          <p className="text-white/90 mb-4">
            If you experience or witness any form of discrimination while using StringsSG, please report it immediately. Your safety and well-being are our priority.
          </p>
          <a 
            href="mailto:support@stringssg.com"
            className="inline-flex items-center gap-2 bg-white text-purple-600 font-semibold px-5 py-3 rounded-lg transition-all hover:bg-white/90"
          >
            <FaEnvelope />
            support@stringssg.com
          </a>
        </div>
      </LegalPageLayout>

      <Script 
        id="google-tagmanager-new"
        src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
        strategy="lazyOnload"
        onLoad={() => {              
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-QD1HGGHDYJ');
        }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
        {`!function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '128634041116395');
          fbq('track', 'PageView');`}
      </Script>
    </>
  )
}
